import {
  getPendingCartChanges,
  clearPendingCartChanges,
} from "./pendingCartChanges";

import {
  AddProductToCart,
  GetProductsInCartByUserId,
  UpdateProduct,
  RemoveProductFromCart,
} from "../utils/productInCart";

const getItemKey = (
  productId,
  productOptionId
) => {
  const normalizedProductId =
    Number(productId);

  const normalizedOptionId =
    productOptionId == null
      ? "without-option"
      : Number(productOptionId);

  return `${normalizedProductId}-${normalizedOptionId}`;
};

const getGuestCart = () => {
  try {
    const savedCart =
      localStorage.getItem("cart");

    return savedCart
      ? JSON.parse(savedCart)
      : [];
  } catch (error) {
    console.error(
      "שגיאה בקריאת עגלת האורח:",
      error
    );

    return [];
  }
};

const getProductId = (item) => {
  return (
    item.productId ??
    item.ProductId ??
    item.product?.id ??
    item.Product?.Id
  );
};

const getProductOptionId = (item) => {
  return (
    item.productOptionId ??
    item.ProductOptionId ??
    item.productOption?.id ??
    item.ProductOption?.Id ??
    null
  );
};

const getAmount = (item) => {
  return Number(
    item.amount ??
    item.Amount ??
    0
  );
};

export const syncPendingCartChanges =
  async (userId) => {
    /*
     * העגלה המלאה ששמורה בדפדפן.
     */
    const guestCart = getGuestCart();

    /*
     * שינויים שהמשתמשת עשתה כאורחת.
     */
    const pendingChanges =
      getPendingCartChanges();

    /*
     * יצירת רשימת סנכרון מכל העגלה,
     * ולא רק מהשינויים הממתינים.
     */
    const changesByKey = new Map();

    guestCart.forEach((item) => {
      const productId =
        getProductId(item);

      const productOptionId =
        getProductOptionId(item);

      const amount =
        getAmount(item);

      if (
        !productId ||
        !Number.isFinite(amount) ||
        amount <= 0
      ) {
        return;
      }

      const key = getItemKey(
        productId,
        productOptionId
      );

      changesByKey.set(key, {
        type: "SET_AMOUNT",
        productId,
        productOptionId,
        amount,
      });
    });

    /*
     * השינויים הממתינים מקבלים עדיפות.
     * לדוגמה, אם מוצר הוסר מהעגלה.
     */
    pendingChanges.forEach((change) => {
      const key = getItemKey(
        change.productId,
        change.productOptionId
      );

      changesByKey.set(
        key,
        change
      );
    });

    const changesToSync =
      Array.from(
        changesByKey.values()
      );

    /*
     * אם אין בכלל עגלת אורח ואין שינויים,
     * מחזירים את העגלה הקיימת מהשרת.
     */
    if (changesToSync.length === 0) {
      return await GetProductsInCartByUserId(
        userId
      );
    }

    const serverResponse =
      await GetProductsInCartByUserId(
        userId
      );

    const serverCart =
      serverResponse?.data ??
      serverResponse ??
      [];

    const serverItemsByKey =
      new Map();

    serverCart.forEach((item) => {
      const productId =
        getProductId(item);

      const productOptionId =
        getProductOptionId(item);

      const key = getItemKey(
        productId,
        productOptionId
      );

      serverItemsByKey.set(
        key,
        item
      );
    });

    /*
     * מעבירים את המוצרים אחד אחרי השני.
     * כך לא שולחים עשרות בקשות באותו רגע.
     */
    for (const change of changesToSync) {
      const key = getItemKey(
        change.productId,
        change.productOptionId
      );

      const existingServerItem =
        serverItemsByKey.get(key);

      if (change.type === "REMOVE") {
        if (existingServerItem?.id) {
          await RemoveProductFromCart(
            existingServerItem.id
          );

          serverItemsByKey.delete(key);
        }

        continue;
      }

      if (change.type !== "SET_AMOUNT") {
        continue;
      }

      const amount =
        Number(change.amount);

      if (
        !Number.isFinite(amount) ||
        amount <= 0
      ) {
        if (existingServerItem?.id) {
          await RemoveProductFromCart(
            existingServerItem.id
          );

          serverItemsByKey.delete(key);
        }

        continue;
      }

      if (existingServerItem) {
        const updatedItem = {
          id: existingServerItem.id,
          amount,
          customerId: userId,
          productId:
            getProductId(
              existingServerItem
            ),
          productOptionId:
            getProductOptionId(
              existingServerItem
            ),
          product: null,
          productOption: null,
        };

        await UpdateProduct(
          existingServerItem.id,
          updatedItem
        );
      } else {
        await AddProductToCart({
          customerId: userId,
          productId:
            change.productId,
          productOptionId:
            change.productOptionId ??
            null,
          amount,
        });
      }
    }

    /*
     * מביאים מחדש את העגלה מהשרת
     * ובודקים שכל המוצרים נשמרו.
     */
    const finalResponse =
      await GetProductsInCartByUserId(
        userId
      );

    const finalServerCart =
      finalResponse?.data ??
      finalResponse ??
      [];

    const finalItemsByKey =
      new Map();

    finalServerCart.forEach((item) => {
      const key = getItemKey(
        getProductId(item),
        getProductOptionId(item)
      );

      finalItemsByKey.set(
        key,
        item
      );
    });

    const failedChanges =
      changesToSync.filter(
        (change) => {
          const key = getItemKey(
            change.productId,
            change.productOptionId
          );

          const savedItem =
            finalItemsByKey.get(key);

          if (
            change.type === "REMOVE"
          ) {
            return Boolean(savedItem);
          }

          if (
            change.type ===
            "SET_AMOUNT"
          ) {
            return (
              !savedItem ||
              getAmount(savedItem) !==
                Number(change.amount)
            );
          }

          return false;
        }
      );

    /*
     * אם מוצר לא נשמר, לא מוחקים
     * את השינויים המקומיים.
     */
    if (failedChanges.length > 0) {
      console.error(
        "מוצרים שלא נשמרו:",
        failedChanges
      );

      throw new Error(
        `${failedChanges.length} מוצרים לא נשמרו בעגלה`
      );
    }

    /*
     * מנקים רק לאחר שווידאנו
     * שכל המוצרים נשמרו בשרת.
     */
    clearPendingCartChanges();

    return finalServerCart;
  };